import { Box, CircularProgress, FormControl, Input, InputLabel, Stack } from "@mui/material";
import { useState } from "react";
import { useAuth } from "../../context/Auth";
import { useNavigate, useParams } from "react-router-dom";
import { instance } from "../../utils/axios-config";
import { checkDoublePattern, checkIntPattern } from "../../utils/commons";
import ModalInfo from "../ModalInfo";

const FormEndComponent = ({position, setPosition, state, setState}) => {

    const {sessionToken} = useAuth();
    const {id} = useParams();
    const navigate = useNavigate();

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [ errorMsg, setErrorMsg ] = useState({title: "¡Vaya! Parece que algo ha ido mal.", content: "Algo ha fallado al intentar crear el sector. Por favor, vuelve a intentarlo más tarde."});


    const handlePrev = (e) => {
        e.preventDefault();
        setPosition(4);
    }

    const handleSend = async (e) => {
        e.preventDefault();
        if(validateForm()){
            try {
                setLoading(true);
                const data = formatStateData();
                instance.defaults.headers.common = {'Authorization': `Bearer ${sessionToken}`}
                await instance.post(`/sectores/create-sector/${id}`, data).then((resp) => {
                    setLoading(false);
                    //El backend puede devolver avisos (p. ej. sensores no disponibles): el sector se ha creado igualmente
                    const avisos = resp?.data?.avisos?.length ? " Aviso: " + resp.data.avisos.join(" ") : "";
                    navigate("/listaSectores/" + id, {state: {type: "action-new", text: "Se ha creado el sector correctamente." + avisos}});
                });
            } catch (error) {
                if(error.response?.data.message === "error sensores"){
                    setErrorMsg((prevErrorMsg) => ({
                        ...prevErrorMsg,
                        content: "Los sensores del sector no están disponibles en este momento y no se puede obtener la información necesaria para crear el sector y calcular la programación de riego. Por favor, inténtelo de nuevo más tarde."
                    }));
                }else if(error.response?.data.message === "error prediccion"){
                    //(El backend ya no envía "error municipio" ni "error precipitacion": todo fallo de la predicción llega como "error prediccion")
                    setErrorMsg((prevErrorMsg) => ({
                        ...prevErrorMsg,
                        content: "Se ha producido un error al intentar recuperar la predicción meteorológica necesaria para crear el sector. Puede deberse a un fallo temporal del servicio meteorológico. Por favor, inténtelo de nuevo más tarde."
                    }));
                }else if(error.response?.data.message === "error rosetta"){
                    setErrorMsg((prevErrorMsg) => ({
                        ...prevErrorMsg,
                        content: "Se ha producido un error al intentar realizar el cálculo a partir del análisis del suelo. Puede deberse a una indisponibilidad temporal del servicio externo que hace el cálculo. Por favor, inténtelo de nuevo más tarde o elija otra estrategia de captura de datos del suelo."
                    }));
                }else if(error.response?.data.message === "error historico"){
                    setErrorMsg((prevErrorMsg) => ({
                        ...prevErrorMsg,
                        content: "Se ha producido un error al intentar recuperar el histórico de datos climáticos de la estación asociada. Puede deberse a una indisponibilidad temporal del servicio meteorológico o a que se han creado varios sectores en el mismo minuto. Por favor, inténtelo de nuevo en unos minutos."
                    }));
                }else{
                    setErrorMsg((prevErrorMsg) => ({
                        ...prevErrorMsg,
                        content: "Algo ha fallado al intentar crear el sector. Por favor, vuelve a intentarlo más tarde."
                    }));
                }
                setOpen(true);
                setLoading(false);
            }
        }
    }

    const formatStateData = () => {
        let data = {
            nombre: state.nombre,
            hRiegoAplicadas: state.hRiegoAplicadas ? parseInt(state.hRiegoAplicadas) : null,
            tipoUsuario: state.tipoUsuario,
            metodoCalculoPE: state.metodoCalculoPE,
            tipoCultivo: state.tipoCultivo,
            distanciaArboles: parseFloat(state.distanciaArboles?.replace(',', '.')),
            distanciaLineas: parseFloat(state.distanciaLineas?.replace(',', '.')),
            diametroCopa: parseFloat(state.diametroCopa?.replace(',', '.')),
            seto: state.seto === "true" ? true : state.seto === "false" ? false : null,
            tipoRiego: state.tipoRiego,
            nRamales: state.nRamales ? parseInt(state.nRamales) : null,
            distanciaGoteros: state.distanciaGoteros ? parseFloat(state.distanciaGoteros?.replace(',', '.')) : null,
            nGoteros: state.nGoteros ? parseInt(state.nGoteros): null,
            caudalGotero: parseFloat(state.caudalGotero?.replace(',', '.')),
            superficieSector: parseFloat(state.superficieSector?.replace(',', '.')),
            tipoEstrategia: state.tipoCultivo === "fresa" ? null : state.tipoEstrategia,   //Fresa no tiene estrategia
            riegoACubrir: state.riegoACubrir ? parseFloat(state.riegoACubrir): null,
            limiteLV: parseInt(state.limiteLV),
            limiteSD: parseInt(state.limiteSD),
           //dotacionRiego: parseFloat(state.dotacionRiego),
            conductividad: parseFloat(state.conductividad?.replace(',', '.')),
            textura: state.analisis === "textura" ? state.textura : null,
            analisis: state.analisis,
            ptArena: state.analisis === "analisis" ? (state.ptArena ? parseFloat(state.ptArena?.replace(',', '.')) : null) : null,
            ptLimo: state.analisis === "analisis" ? (state.ptLimo ? parseFloat(state.ptLimo?.replace(',', '.')) : null) : null,
            ptArcilla: state.analisis === "analisis" ? (state.ptArcilla ? parseFloat(state.ptArcilla?.replace(',', '.')) : null) : null,
            densidad: state.analisis === "analisis" ? (state.densidad ? parseFloat(state.densidad?.replace(',', '.')) : null) : null,
            pmp: state.analisis === "manual" ? (state.pmp ? parseFloat(state.pmp?.replace(',', '.')) : null) : null,
            cc: state.analisis === "manual" ? (state.cc ? parseFloat(state.cc?.replace(',', '.')) : null) : null,
            humedadSueloInicial: state.humedadSueloInicial,
            tipoPlantacion: state.tipoPlantacion,
            //Solo Usuario 4.0 (evita guardar un valor tecleado antes de cambiar el tipo de usuario)
            distanciaFinRamal: state.tipoUsuario === "Usuario 4.0" && state.distanciaContador ? parseFloat(state.distanciaContador?.replace(',', '.')) : null,
            //Las credenciales de los sensores solo se usan en el navegador para verificar el código de estación
            codigoEstacion: state.codigoEstacion,
            idEstacion: state.idEstacion,
            fechaSiembra: state.fechaSiembra,
            fechaCubricion: state.fechaCubricion,
            fechaFinCampaña: state.fechaFinCampaña,
            capacidadRetencion: state.capacidadRetencion,
            //distanciaFinRamal: parseFloat(state.distanciaFinRamal?.replace(',', '.')),
            tiposMTunel: state.tipoCultivo === "fresa" ? state.tiposMTunel : null,         //Solo fresa tiene macrotúneles
            encalado: state.encalado === "S" ? 1 : 0,   //El selector usa "S"/"N" (parseInt daba NaN y se guardaba siempre 0)
            dosisEncalado: state.dosisEncalado ? parseFloat(state.dosisEncalado?.replace(',', '.')) : null,
            fechaEncalado: state.fechaEncalado,
            fechaDesencalado: state.fechaDesencalado,
            pendienteCubierta: state.pendienteCubierta ? parseFloat(state.pendienteCubierta?.replace(',', '.')) : null,
            //tuberiasLaterales: state.tuberiasLaterales ? parseInt(state.tuberiasLaterales) : null,
            sepLineasRiego: state.sepLineasRiego ? parseFloat(state.sepLineasRiego?.replace(',', '.')) : null,
            uniRedRiego: state.uniRedRiego ? parseFloat(state.uniRedRiego?.replace(',', '.')) : null
        };

        return data;
    }

    const handleChangeHorasAplicadas = (e) => {
        if(checkIntPattern(e)){
            setState({...state, hRiegoAplicadas: e});
        }
    }

    const handleChangeDistanciaContador = (e) => {
        if(checkDoublePattern(e)){
            setState({...state, distanciaContador: e});
        }
    }


    const validateForm = () =>{
        setErrors({});
        let inputIds = "";
        let valid = true;
        
        if(state.tipoUsuario !== "Usuario 4.0" && !(checkIntPattern(state.hRiegoAplicadas ?? ""))){
            setErrors(errors => ({...errors, errorHRiegoAplicadas: true, errorHRiegoAplicadasMsg: "Debe ser un valor numérico."}));
            inputIds = inputIds ? inputIds +",hRiegoAplicadas" : "hRiegoAplicadas";
            valid = false;
        }
        //Usuario 4.0: la distancia del contador es obligatoria (antes la condición nunca se cumplía)
        if(state.tipoUsuario === "Usuario 4.0" && (state.distanciaContador === undefined || state.distanciaContador === '' || !(checkDoublePattern(state.distanciaContador)))){
            setErrors(errors => ({...errors, errorDistanciaContador: true, errorDistanciaContadorMsg: state.distanciaContador ? "Debe ser un valor numérico." : "Debe indicar la distancia del contador al final del ramal."}));
            inputIds = inputIds ? inputIds +",distanciaContador" : "distanciaContador";
            valid = false;
        }
        if(inputIds !== ""){
            try{
                let elem = document.getElementById(inputIds.split(",")[0]);
                elem.scrollIntoView({behavior: "smooth"});
                elem.focus();
            }catch(err){

            }
        }
        return valid;
    }

    return(
        <>
            <Box className={position !== 5 && 'hide'}>
                {state.tipoUsuario !== "Usuario 4.0" ?
                    <>
                        <h2>Riego aplicado</h2>
                        <FormControl sx={{width: "100%", marginBottom: "20px"}} variant="standard">
                            <InputLabel htmlFor="hRiegoAplicadas">Horas de Riego aplicadas hasta la fecha</InputLabel>
                            <Input
                                id="hRiegoAplicadas"
                                label="Horas de riego aplicadas hasta la fecha"
                                value={state.hRiegoAplicadas}
                                onChange={(e) => handleChangeHorasAplicadas(e.target.value)}
                                name="hRiegoAplicadas"
                                error={errors.errorHRiegoAplicadas}
                            />
                            {errors.errorHRiegoAplicadasMsg && <div className="errorInput">{errors.errorHRiegoAplicadasMsg}</div>}
                        </FormControl>
                    </>
                    :
                    <>
                        <h2>Datos del contador del ramal</h2>
                        <FormControl sx={{width: "100%", marginBottom: "20px"}} variant="standard">
                            <InputLabel htmlFor="distanciaContador">Distancia del contador al final del ramal (m)*</InputLabel>
                            <Input
                                id="distanciaContador"
                                label="Distancia del contador al final del ramal (m)"
                                value={state.distanciaContador}
                                onChange={(e) => handleChangeDistanciaContador(e.target.value)}
                                name="distanciaContador"
                                error={errors.errorDistanciaContador}
                            />
                            {errors.errorDistanciaContadorMsg && <div className="errorInput">{errors.errorDistanciaContadorMsg}</div>}
                        </FormControl>
                    </>
                }
                
                {loading ?
                    <Box align="center" sx={{my: 2}}>
                        <CircularProgress />
                    </Box>
                    :
                    <Stack spacing={2} direction="row" justifyContent="space-between" className="tic4bio-sectores-btns">
                        <button className="btn white" onClick={(e) => handlePrev(e)}>Anterior</button>
                        <button className="btn green" onClick={(e) => handleSend(e)} style={{margin: "16px 0"}}>Crear</button>
                    </Stack>
                }
            </Box>
            <ModalInfo open={open} setOpen={setOpen} contentTitle={errorMsg.title} contentText={errorMsg.content}/>
        </>
    );
}

export default FormEndComponent;